// ---------------------------------------------------------------------------
// Quode design tokens for the plugin layer.
//
// Mirrors branding/DESIGN.md. The theme exposes the same values as CSS custom
// properties (see theme/brand.css); this module exists because the assessment
// pages build inline styles and canvas/chart colours in JS, where `var(--q-*)`
// cannot be interpolated.
// ---------------------------------------------------------------------------

/** Three-state semantic status used by pills and badges. */
export type Status = 'ok' | 'warn' | 'info' | 'idle';

export const q = {
  // Canvas
  midnight: '#060A14',
  surface: '#0C1322',
  surface2: '#131E32',
  border: 'rgba(255,255,255,0.07)',

  // Brand — emerald is the single accent
  emerald: '#10B981',
  mint: '#6EE7B7',
  glow: '#34D399',
  forest: '#022C22',
  deep: '#022C22',

  // Text
  ghost: '#F1F5F9',
  slate: '#94A3B8',
  slateDim: '#6E7F96',

  // Semantic
  cyan: '#06B6D4',
  red: '#EF4444',
  orange: '#F97316',

  // Amber is reserved by the brand for the cursor lens dot. It is kept here for
  // the "medium" severity/effort step, which needs a warm tone distinct from
  // both orange (P1) and red (P0).
  amber: '#F59E0B',
  amberBright: '#FCD34D',

  // The brand forbids purple, so the one `violet` tone maps onto cyan.
  violet: '#06B6D4',
} as const;

/** Reusable visual effects, kept in one place so surfaces stay consistent. */
export const fx = {
  /** Hairline divider. */
  hair: `1px solid ${q.border}`,
  /** Focus / selection ring in the brand accent. */
  ring: `0 0 0 2px ${q.emerald}`,
  /** Frosted panel over the midnight canvas. */
  glass: `linear-gradient(160deg, rgba(255,255,255,0.045), rgba(255,255,255,0.012))`,
  blur: 'saturate(140%) blur(14px)',
  /** Soft emerald bloom for emphasis. */
  glowSoft: `0 0 24px rgba(16,185,129,0.18)`,
  /** Emerald sweep used on hero and feature cards. */
  accentSweep: `linear-gradient(120deg, ${q.surface2} 0%, ${q.surface} 45%, ${q.midnight} 100%)`,
  /** Faint aurora band along the top edge of a hero. */
  auroraTop: `radial-gradient(120% 100% at 78% -10%, rgba(16,185,129,0.18) 0%, transparent 60%)`,
} as const;

export const statusColor: Record<Status, string> = {
  ok: q.emerald,
  warn: q.orange,
  info: q.cyan,
  idle: q.slateDim,
};
